/**
 * Online Radio UI: meta on load, autoplay when a station is selected.
 */

import { createPcmOutput } from "./pcm-output.js";
import { formatLinkQuality } from "../shared/fps-connection.js";

/** @type {Worker|null} */
let worker = null;
/** @type {AudioContext|null} */
let audioContext = null;
/** @type {Awaited<ReturnType<typeof createPcmOutput>>|null} */
let pcmOut = null;

let metaConnected = false;
let audioStarted = false;
let paused = false;
let muted = false;
/** AudioWorklet starvation is local output trouble, not a FastPubSub packet loss. */
let audioOutputUnderruns = 0;
let metaSampleRate = 48000;
/** Stream parameters from meta snapshot. */
let wireMeta = {
  sampleRate: 48000,
  frameMs: 60,
  channels: 2,
  bitrate: 128000
};
let stations = [];
let selectedStationId = null;
let pendingProtectedStation = null;
/** Passwords for protected stations in this browser tab session. */
const stationPasswords = new Map();
/** Start playback after station pick (including ?station=). */
let pendingAutoplay = false;
let autoplayInFlight = false;
/** songId and duration for progress bar */
let progressSongId = 0;
let progressDurationMs = 0;
/** Last known output channel count (workletReady must not reset this to 1). */
let lastOutChannels = 2;
let displayedSongId = 0;

const statusEl = document.getElementById("status");
const btnPlay = document.getElementById("btn-play");
const btnPause = document.getElementById("btn-pause");
const btnMute = document.getElementById("btn-mute");
const volumeEl = document.getElementById("volume");
const stationListEl = document.getElementById("station-list");
const coverEl = document.getElementById("cover");
const coverPlaceholder = document.getElementById("cover-placeholder");
const titleEl = document.getElementById("track-title");
const artistEl = document.getElementById("track-artist");
const streamInfoEl = document.getElementById("stream-info");
const progressBar = document.getElementById("progress-bar");
const statEdge = document.getElementById("stat-edge");
const statWsRtt = document.getElementById("stat-ws-rtt");
const statFps = document.getElementById("stat-fps");
const statTenant = document.getElementById("stat-tenant");
const statMetaCh = document.getElementById("stat-meta-ch");
const statAudioCh = document.getElementById("stat-audio-ch");
const statWire = document.getElementById("stat-wire");
const statAudioOut = document.getElementById("stat-audio-out");
const statBuffer = document.getElementById("stat-buffer");
const statSong = document.getElementById("stat-song");
const statRecv = document.getElementById("stat-recv");
const statPos = document.getElementById("stat-pos");
const statLost = document.getElementById("stat-lost");
const statGaps = document.getElementById("stat-gaps");
const statDecode = document.getElementById("stat-decode");
const statDecodeLate = document.getElementById("stat-decode-late");
const statFec = document.getElementById("stat-fec");
const statDup = document.getElementById("stat-dup");
const statOld = document.getElementById("stat-old");
const statParse = document.getElementById("stat-parse");
const statEvict = document.getElementById("stat-evict");
const statSilence = document.getElementById("stat-silence");
const statDecoded = document.getElementById("stat-decoded");
const statSdkQueue = document.getElementById("stat-sdk-queue");
const statSdkEvents = document.getElementById("stat-sdk-events");
const statDecodeOrder = document.getElementById("stat-decode-order");
const statAudioUnderrun = document.getElementById("stat-audio-underrun");
const statLastDiagnostic = document.getElementById("stat-last-diagnostic");
const fpsStatusEl = document.getElementById("fps-status");
const fpsLabelEl = document.getElementById("fps-label");
let fpsConnectionState = "connecting";
let fpsConnectionDetail = "connecting…";
let linkQualityLabel = "ws rtt …";
/** Selected edge from worker, e.g. `edge-fra-01 (12 ms)`. */
let selectedEdgeLabel = "";
const passwordDialog = document.getElementById("password-dialog");
const passwordForm = document.getElementById("password-form");
const passwordHint = document.getElementById("password-hint");
const passwordInput = document.getElementById("password-input");

btnPlay.addEventListener("click", onPlay);
btnPause.addEventListener("click", onPause);
btnMute.addEventListener("click", onMute);
volumeEl.addEventListener("input", onVolume);
passwordForm.addEventListener("submit", onPasswordSubmit);

/**
 * On page load: worker + meta channel.
 */
function boot() {
  setFpsConnection("connecting", "connecting…");
  setStatus("connecting to meta…", true);
  worker = new Worker(new URL("./radio-worker.js", import.meta.url), { type: "module" });
  worker.onmessage = onWorkerMessage;
  worker.onerror = (event) => {
    setFpsConnection("error", "worker error");
    setStatus(`worker error: ${event.message}`, false);
  };
  worker.postMessage({ type: "connectMeta" });
}

/**
 * Play: AudioContext + subscription to selected station stream.
 */
async function onPlay() {
  if (audioStarted) {
    if (paused) {
      onPause();
      return;
    }
    if (audioContext?.state === "suspended") {
      await audioContext.resume();
    }
    setStatus("playing", true);
    return;
  }

  if (!metaConnected) {
    setStatus("waiting for meta…", true, "warn");
    return;
  }
  if (selectedStationId === null) {
    setStatus("select a station", true, "warn");
    return;
  }

  setStatus("starting audio…", true);
  btnPlay.disabled = true;

  try {
    audioContext = new AudioContext({ sampleRate: metaSampleRate });
    pcmOut = await createPcmOutput(
      audioContext,
      (count) => {
        audioOutputUnderruns = count;
        updateAudioOutStats();
        updateAudioUnderrunDiagnostic();
      },
      (workletHz) => {
        const hz = Math.round(workletHz || audioContext.sampleRate);
        worker?.postMessage({ type: "setOutputSampleRate", sampleRate: hz });
        updateAudioOutStats();
      }
    );
    onVolume();
    updateAudioOutStats();

    worker.postMessage({
      type: "startAudio",
      outputSampleRate: audioContext.sampleRate
    });
    await audioContext.resume();

    if (Math.abs(audioContext.sampleRate - metaSampleRate) > 1) {
      console.warn(
        `[radio UI] AudioContext ${audioContext.sampleRate} Hz (requested ${metaSampleRate}) — worker resample`
      );
    }

    if (audioContext.state !== "running") {
      throw new Error(`AudioContext not running: ${audioContext.state}`);
    }

    audioStarted = true;
    btnPause.disabled = false;
    btnMute.disabled = false;

    if (pcmOut.mode === "buffer") {
      setStatus("playing (HTTP fallback)", true);
    }
  } catch (error) {
    audioContext?.close();
    audioContext = null;
    pcmOut = null;
    btnPlay.disabled = false;
    setFpsConnection("error", "audio");
    setStatus(error.message || String(error), false);
  }
}

function onPause() {
  if (!audioStarted) {
    return;
  }
  paused = !paused;
  if (paused) {
    worker?.postMessage({ type: "pauseAudio" });
    pcmOut?.flush();
    btnPause.textContent = "Resume";
    setStatus("paused (stream off)", true);
  } else {
    worker?.postMessage({ type: "resumeAudio" });
    pcmOut?.flush();
    btnPause.textContent = "Pause";
    setStatus("buffering…", true, "warn");
  }
}

function onMute() {
  if (!audioStarted) {
    return;
  }
  muted = !muted;
  pcmOut?.setMute(muted);
  btnMute.textContent = muted ? "Unmute" : "Mute";
}

function onVolume() {
  const gain = Number(volumeEl.value) / 100;
  pcmOut?.setGain(gain);
}

/**
 * @param {MessageEvent} event
 */
function onWorkerMessage(event) {
  const msg = event.data;
  switch (msg.type) {
    case "connecting":
      setFpsConnection("connecting", msg.reason ? "reconnect…" : "connecting…");
      setStatus(msg.reason ? `reconnect (${msg.reason})…` : "connecting to meta…", true);
      break;
    case "ready":
      metaConnected = true;
      void tryAutoplay();
      if (msg.edge) {
        selectedEdgeLabel = msg.edge;
      }
      statEdge.textContent = selectedEdgeLabel || "—";
      statTenant.textContent = msg.tenant || "—";
      statMetaCh.textContent = msg.metaChannel ? `sub ${msg.metaChannel}` : "—";
      statFps.textContent = "meta online";
      if (msg.tokenTtlSec) {
        console.info(`[radio UI] AT TTL ${msg.tokenTtlSec}s`);
      }
      if (msg.reason === "token reissue") {
        setFpsConnection("meta", "meta · token reissue");
        setStatus("новый AT, meta переподключена", true);
      } else {
        setFpsConnection("meta", "meta · online");
        setStatus("meta connected — waiting for station catalog…", true);
      }
      break;
    case "meta":
      updateWireMeta(msg.meta);
      renderMeta(msg.meta);
      refreshNowPlayingCard();
      updateStreamDisplay();
      statFps.textContent = audioStarted ? "meta+audio" : "meta live";
      if (!audioStarted) {
        const count = msg.meta.stations?.length ?? 0;
        setFpsConnection("meta", `meta · ${count} st`);
        if (!pendingAutoplay) {
          setStatus("select a station", true);
        }
        void tryAutoplay();
      }
      break;
    case "nowPlaying":
      if (msg.station && selectedStationId !== null
          && Number(msg.station.id) === Number(selectedStationId)) {
        mergeStationMeta(msg.station);
        updateNowPlaying(msg.station);
      }
      break;
    case "stationPicked":
      selectedStationId = msg.stationId;
      renderMeta({ stations });
      updateStreamDisplay();
      btnPlay.disabled = false;
      requestAutoplay();
      break;
    case "stationSelected":
      statAudioCh.textContent = msg.streamChannel ? `sub ${msg.streamChannel}` : "—";
      setFpsConnection("audio", `audio · ${msg.streamChannel || "ok"}`);
      break;
    case "audioStarted":
      btnPlay.disabled = false;
      btnPlay.textContent = "Play";
      statAudioCh.textContent = msg.streamChannel ? `sub ${msg.streamChannel}` : "—";
      updateAudioOutStats(msg.channels);
      statFps.textContent = "meta+audio";
      setFpsConnection("audio", `audio · ${msg.streamChannel || "ok"}`);
      setStatus("buffering…", true, "warn");
      break;
    case "pcm":
      pcmOut?.pushPcm(msg.samples, msg.channels || 1);
      break;
    case "pcm_flush":
      pcmOut?.flush();
      break;
    case "buffering":
      if (audioStarted) {
        setStatus("buffering…", true, "warn");
      }
      break;
    case "playing":
      setFpsConnection("audio", "audio · playing");
      setStatus("playing", true);
      break;
    case "audioPaused":
      statRecv.textContent = "—";
      setFpsConnection("audio", "audio · paused");
      break;
    case "audioResumed":
      statAudioCh.textContent = msg.streamChannel ? `sub ${msg.streamChannel}` : "—";
      setFpsConnection("audio", "audio · buffering");
      break;
    case "stats":
      renderStats(msg);
      break;
    case "link_quality":
      linkQualityLabel = formatLinkQuality(msg);
      if (statWsRtt) {
        statWsRtt.textContent = linkQualityLabel;
      }
      refreshFpsHeader();
      break;
    case "transport_diag":
      renderStats({ ...msg, lastDiagnostic: msg.message });
      // rttMeasured is noisy; keep real problems visible.
      if (msg.message && msg.message !== "rttMeasured") {
        console.warn(`[radio UI] SDK diagnostic: ${msg.message}`);
      }
      break;
    case "chunk_gap":
      renderStats(msg);
      console.warn(
        `[radio UI] gap ${msg.source}: song_id=${msg.songId} ` +
          `${msg.fromMs}→${msg.toMs} ms (−${msg.gapChunks} ch) — ${msg.reason}`
      );
      break;
    case "chunk_evicted":
      renderStats(msg);
      console.warn(`[radio UI] evicted: ${msg.key}`);
      break;
    case "decode_error":
      renderStats(msg);
      console.warn(
        `[radio UI] decode song_id=${msg.songId} pos=${msg.positionMs}: ${msg.message}`
      );
      setStatus(`decode: ${msg.message}`, true, "warn");
      break;
    case "needPassword":
      pendingProtectedStation = msg.station;
      passwordHint.textContent = msg.station.passwordHint
        ? `Hint: ${msg.station.passwordHint}`
        : "Enter password";
      passwordInput.value = "";
      passwordDialog.showModal();
      break;
    case "decrypt_error":
      setFpsConnection("error", "decrypt");
      setStatus(msg.message || "decrypt error", false);
      break;
    case "error":
      setFpsConnection("error", "error");
      setStatus(msg.message || "error", false);
      btnPlay.disabled = false;
      break;
    default:
      break;
  }
}

/**
 * Shows wire → ctx Hz, channels, and whether worker resamples.
 * @param {number} [channels]
 */
function updateAudioOutStats(channels) {
  if (!audioContext || !pcmOut) {
    statAudioOut.textContent = "—";
    statAudioOut.className = "";
    return;
  }

  const wireHz = metaSampleRate;
  const ctxHz = Math.round(audioContext.sampleRate);
  if (channels) {
    lastOutChannels = channels;
  }
  const ch = lastOutChannels;
  const resample = Math.abs(ctxHz - wireHz) > 1;
  const mode = pcmOut.mode;

  statAudioOut.textContent =
    `${wireHz}→${ctxHz} Hz · ${ch} ch · ${mode}` +
    (resample ? " · resample" : " · direct") +
    (audioOutputUnderruns > 0 ? ` · underrun ${audioOutputUnderruns}` : "");
  statAudioOut.className = resample || audioOutputUnderruns > 0 ? "warn" : "";
  statAudioOut.title =
    resample
      ? `Worker resample ${wireHz}→${ctxHz} Hz (may reduce audio quality)`
      : audioOutputUnderruns > 0
        ? `AudioWorklet did not receive PCM in time ${audioOutputUnderruns} times`
        : `Wire and AudioContext match (${wireHz} Hz)`;
}

/** AudioWorklet underflow is a local output failure, separate from packet loss. */
function updateAudioUnderrunDiagnostic() {
  statAudioUnderrun.textContent = String(audioOutputUnderruns);
  statAudioUnderrun.className = audioOutputUnderruns > 0 ? "warn" : "";
  if (audioOutputUnderruns > 0) {
    statLastDiagnostic.textContent = `AudioWorklet underrun ×${audioOutputUnderruns}: output inserted silence`;
    statLastDiagnostic.className = "warn";
  }
}

/**
 * Formats recv/s.
 * @param {number|undefined} value
 * @param {number} [digits=1]
 * @returns {string}
 */
function formatRecvRate(value, digits = 1) {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "—";
  }
  return Number(value).toFixed(digits);
}

/**
 * Highlights dropped-message counter.
 * @param {HTMLElement} el
 * @param {number} value
 */
function renderDropStat(el, value) {
  el.textContent = String(value);
  el.className = value > 0 ? "warn" : "";
}

/**
 * Updates stats block (including chunk losses).
 * @param {object} msg
 */
function renderStats(msg) {
  if (msg.bufferMs !== undefined || msg.queuedMs !== undefined) {
    const cont = msg.bufferMs ?? 0;
    const pcmReady = msg.pcmReadyMs ?? 0;
    const queued = msg.queuedMs ?? 0;
    const chunks = msg.queuedChunks ?? 0;
    const lag = msg.lagMs ?? 0;
    statBuffer.textContent =
      `${cont} ms · pcm ${pcmReady} ms · q ${queued} ms · lag ${lag} ms (${chunks} ch)`;
    statBuffer.className = lag > 800 || pcmReady < 120 ? "warn" : "";
  }
  if (msg.songId !== undefined) {
    statSong.textContent = String(msg.songId);
  }
  if (msg.recvPerSecRecent !== undefined || msg.recvPerSecSong !== undefined) {
    const recent = formatRecvRate(msg.recvPerSecRecent);
    const song = formatRecvRate(msg.recvPerSecSong, 2);
    const raw = formatRecvRate(msg.recvPerSecRaw);
    const idleMs = msg.recvIdleMs ?? 0;
    const skipped = (msg.recvBadParse ?? 0) + (msg.recvOldSong ?? 0);
    let line = `${recent} · ${song}`;
    const rawNum = msg.recvPerSecRaw ?? 0;
    const recentNum = msg.recvPerSecRecent ?? 0;
    if (rawNum - recentNum > 0.4) {
      line += ` · raw ${raw}`;
    }
    if (idleMs > 80) {
      line += ` (+${Math.round(idleMs)} ms)`;
      statRecv.className = "warn";
    } else {
      statRecv.className = skipped > 0 ? "warn" : "";
    }
    statRecv.textContent = line;
    statRecv.title = "chunks/s over 1 s · track average · raw WS/s";
  }
  if (msg.positionMs !== undefined) {
    const chunk = msg.chunkIndex !== undefined ? `#${msg.chunkIndex}` : "";
    statPos.textContent = chunk
      ? `${msg.positionMs} ms · ${chunk}`
      : `${msg.positionMs} ms`;
    if (audioStarted && selectedStationId !== null) {
      let durationMs = progressDurationMs;
      if (durationMs <= 0 && msg.maxRecvMs > 0) {
        durationMs = msg.maxRecvMs + wireMeta.frameMs;
      }
      updateProgressBar(msg.positionMs, durationMs, msg.songId ?? progressSongId);
    }
  }

  const lost = msg.lostChunks ?? 0;
  const lostMs = msg.lostMs ?? 0;
  statLost.textContent = lost > 0 ? `${lost} (−${lostMs} ms)` : "0";
  statLost.className = lost > 0 ? "warn" : "";

  const recvGaps = msg.recvGaps ?? 0;
  const playGaps = msg.playGaps ?? 0;
  const recvReorder = msg.recvReorder ?? 0;
  statGaps.textContent = `recv ${recvGaps} / play ${playGaps} / late ${recvReorder}`;
  statGaps.className = recvGaps + playGaps > 0 ? "warn" : "";

  const decodeErr = msg.decodeErrors ?? 0;
  statDecode.textContent = String(decodeErr);
  statDecode.className = decodeErr > 0 ? "warn" : "";

  renderDropStat(statDecodeLate, msg.decodeLate ?? 0);
  renderDropStat(statFec, msg.fecRecover ?? 0);

  const decoded = msg.decodedFrames ?? 0;
  statDecoded.textContent = String(decoded);

  const silence = msg.silenceFrames ?? 0;
  statSilence.textContent = String(silence);
  statSilence.className = silence > 0 ? "warn" : "";

  renderDropStat(statDup, msg.duplicates ?? 0);
  renderDropStat(statOld, msg.recvOldSong ?? 0);
  renderDropStat(statParse, msg.recvBadParse ?? 0);
  renderDropStat(statEvict, msg.evicted ?? 0);

  const queueDrops = msg.sdkQueueFull ?? 0;
  const audioQueueDrops = msg.sdkAudioQueueFull ?? 0;
  const metaQueueDrops = msg.sdkMetaQueueFull ?? 0;
  const queueDepth = msg.sdkQueueDepth ?? 0;
  const queueCapacity = msg.sdkQueueCapacity ?? 256;
  statSdkQueue.textContent = `audio ${audioQueueDrops} · meta ${metaQueueDrops} · ${queueDepth}/${queueCapacity}`;
  statSdkQueue.className = queueDrops > 0 ? "warn" : "";
  statSdkQueue.title = `all queue overflows ${queueDrops}; audio ${audioQueueDrops}; meta ${metaQueueDrops}; current audio queue depth ${queueDepth} of ${queueCapacity}`;

  const sdkErrors = msg.sdkErrors ?? 0;
  const reconnects = msg.sdkReconnects ?? 0;
  const filterErrors = msg.sdkInboundFilterErrors ?? 0;
  const duplicateDrops = msg.sdkDuplicateDropped ?? 0;
  const transportErrors = msg.sdkTransportErrors ?? 0;
  statSdkEvents.textContent = `err ${sdkErrors} · reconnect ${reconnects} · filter ${filterErrors} · dup ${duplicateDrops}`;
  statSdkEvents.className = sdkErrors + reconnects + filterErrors + duplicateDrops + transportErrors > 0 ? "warn" : "";
  statSdkEvents.title = `all SDK errors ${sdkErrors}; transport ${transportErrors}; inbound filter ${filterErrors}; reconnects ${reconnects}; overlap duplicates ${duplicateDrops}; empty delivery ${msg.sdkEmptyDeliveries ?? 0}; filter warnings ${msg.sdkFilterWarnings ?? 0}`;

  const decodeLatePackets = msg.decodeLatePackets ?? 0;
  const decodeForwardGaps = msg.decodeForwardGaps ?? 0;
  statDecodeOrder.textContent = `late ${decodeLatePackets} · forward ${decodeForwardGaps}`;
  statDecodeOrder.className = decodeLatePackets + decodeForwardGaps > 0 ? "warn" : "";

  if (msg.lastDiagnostic) {
    statLastDiagnostic.textContent = msg.lastDiagnostic;
    statLastDiagnostic.className = "warn";
  }
}

/**
 * @param {object} meta
 */
function renderMeta(meta) {
  stations = meta.stations || [];
  stationListEl.innerHTML = "";

  if (stations.length === 0) {
    stationListEl.innerHTML = "<li class=\"empty\">no stations in meta</li>";
    return;
  }

  for (const station of stations) {
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = station.online ? "" : "offline";
    if (station.id === selectedStationId) {
      btn.classList.add("active");
    }
    const lock = station.protected ? " 🔒" : "";
    btn.textContent = `${station.name}${lock}`;
    btn.disabled = !station.online;
    btn.addEventListener("click", () => pickStation(station.id));
    li.appendChild(btn);
    stationListEl.appendChild(li);
  }

  refreshNowPlayingCard();

  if (selectedStationId === null && metaConnected) {
    pickStationFromUrl();
  }
}

/**
 * Stores stream parameters from meta snapshot.
 * @param {object} meta
 */
function updateWireMeta(meta) {
  if (!meta) {
    return;
  }
  if (meta.sampleRate) {
    wireMeta.sampleRate = meta.sampleRate;
    metaSampleRate = meta.sampleRate;
  }
  if (meta.frameMs) {
    wireMeta.frameMs = meta.frameMs;
  }
  if (meta.channels) {
    wireMeta.channels = meta.channels;
  }
  if (meta.bitrate) {
    wireMeta.bitrate = meta.bitrate;
  }
}

/**
 * Channel label for UI.
 * @param {number} ch
 */
function formatChannelsLabel(ch) {
  if (ch <= 1) {
    return "mono";
  }
  if (ch === 2) {
    return "stereo";
  }
  return `${ch} ch`;
}

/**
 * Bitrate label for UI.
 * @param {number} bps
 */
function formatBitrateLabel(bps) {
  if (!bps) {
    return "—";
  }
  if (bps >= 1000) {
    return `${Math.round(bps / 1000)} kbps`;
  }
  return `${bps} bps`;
}

/**
 * Stream quality line: Opus, bitrate, Hz, channels, frame_ms.
 * @param {object} [station]
 */
function formatWireStream(station) {
  const parts = [
    "Opus",
    formatBitrateLabel(wireMeta.bitrate),
    `${wireMeta.sampleRate} Hz`,
    formatChannelsLabel(wireMeta.channels),
    `${wireMeta.frameMs} ms`
  ];
  if (station?.protected) {
    parts.push("AES");
  }
  return parts.join(" · ");
}

/**
 * Updates stream quality under track and in stats.
 * @param {object} [station]
 */
function updateStreamDisplay(station) {
  const st =
    station ?? stations.find((s) => s.id === selectedStationId) ?? null;
  if (selectedStationId === null) {
    streamInfoEl.textContent = "—";
    streamInfoEl.hidden = true;
    statWire.textContent = formatWireStream(null);
    return;
  }
  const text = formatWireStream(st);
  streamInfoEl.textContent = text;
  streamInfoEl.hidden = false;
  statWire.textContent = text;
}

/**
 * Updates progress bar from position and duration.
 * position from stats (~480 ms) or meta (~1 s).
 * @param {number} positionMs
 * @param {number} [durationMs]
 * @param {number} [songId]
 */
function updateProgressBar(positionMs, durationMs = 0, songId = 0) {
  if (songId > 0 && songId !== progressSongId) {
    progressSongId = songId;
    progressDurationMs = 0;
  }
  if (durationMs > 0) {
    progressDurationMs = durationMs;
  }
  const denom = Math.max(progressDurationMs, positionMs + 1000, 60000);
  const pct = Math.min(100, Math.max(0, (positionMs / denom) * 100));
  progressBar.style.width = `${pct}%`;
}

/**
 * Merges fresh meta fields into local station list.
 * @param {object} station
 */
function mergeStationMeta(station) {
  const idx = stations.findIndex((s) => Number(s.id) === Number(station.id));
  if (idx >= 0) {
    stations[idx] = station;
  }
}

/**
 * Updates now playing card for selected station.
 */
function refreshNowPlayingCard() {
  if (selectedStationId === null) {
    return;
  }
  const station = stations.find((s) => Number(s.id) === Number(selectedStationId));
  if (station) {
    updateNowPlaying(station);
  }
}

/**
 * @param {object} station
 */
function updateNowPlaying(station) {
  titleEl.textContent = station.title || "—";
  artistEl.textContent = station.artist || "—";
  updateStreamDisplay(station);
  const songChanged = station.songId > 0 && station.songId !== displayedSongId;
  if (songChanged) {
    displayedSongId = station.songId;
    coverEl.removeAttribute("src");
  }
  if (station.coverUrl) {
    coverEl.src = station.coverUrl;
    coverEl.hidden = false;
    coverPlaceholder.hidden = true;
  } else {
    coverEl.hidden = true;
    coverPlaceholder.hidden = false;
  }
  updateProgressBar(station.positionMs, station.durationMs, station.songId);
}

/**
 * Request autoplay after a station is selected.
 */
function requestAutoplay() {
  pendingAutoplay = true;
  void tryAutoplay();
}

/**
 * Start playback when a station is selected and meta is ready.
 */
async function tryAutoplay() {
  if (!pendingAutoplay || audioStarted || autoplayInFlight) {
    return;
  }
  if (selectedStationId === null || !metaConnected) {
    return;
  }
  if (pendingProtectedStation || passwordDialog.open) {
    return;
  }

  autoplayInFlight = true;
  await onPlay();
  autoplayInFlight = false;

  if (audioStarted) {
    pendingAutoplay = false;
    return;
  }
  setStatus("click Play to start", true, "warn");
}

/**
 * Station pick from the list.
 * @param {number|object} stationOrId
 */
function pickStation(stationOrId) {
  const id = typeof stationOrId === "object" ? stationOrId.id : stationOrId;
  const alreadyPlaying =
    audioStarted && !paused && Number(selectedStationId) === Number(id);
  selectedStationId = id;
  refreshNowPlayingCard();
  renderMeta({ stations });

  const station = stations.find((s) => Number(s.id) === Number(id));
  if (!station) {
    return;
  }

  if (alreadyPlaying) {
    return;
  }

  if (station.protected) {
    const remembered = stationPasswords.get(Number(station.id));
    if (remembered) {
      sendPickStation(station, remembered);
      requestAutoplay();
      return;
    }
    pendingProtectedStation = station;
    passwordHint.textContent = station.passwordHint
      ? `Hint: ${station.passwordHint}`
      : "Enter password";
    passwordInput.value = "";
    passwordDialog.showModal();
    return;
  }

  sendPickStation(station);
}

/**
 * @param {object} station
 * @param {string} [password]
 */
function sendPickStation(station, password) {
  if (audioStarted) {
    worker?.postMessage({ type: "selectStation", station, password });
  } else {
    worker?.postMessage({ type: "pickStation", station, password });
  }
}

/**
 * @param {Event} event
 */
function onPasswordSubmit(event) {
  event.preventDefault();
  const station = pendingProtectedStation;
  if (!station) {
    passwordDialog.close();
    return;
  }
  const password = passwordInput.value.trim();
  passwordDialog.close();
  if (password) {
    stationPasswords.set(Number(station.id), password);
  }
  sendPickStation(station, password);
  pendingProtectedStation = null;
}

function pickStationFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("station");
  if (!fromQuery || !stations.length) {
    return;
  }
  const id = Number(fromQuery);
  const station = stations.find((s) => s.id === id);
  if (station?.online) {
    pickStation(station);
  }
}

/**
 * FastPubSub indicator in header.
 * @param {"connecting"|"meta"|"audio"|"error"} state
 * @param {string} label
 */
function setFpsConnection(state, label) {
  fpsConnectionState = state;
  fpsConnectionDetail = label;
  refreshFpsHeader();
}

function refreshFpsHeader() {
  fpsStatusEl.className = `fps-status state-${fpsConnectionState}`;
  const edge = selectedEdgeLabel ? `${selectedEdgeLabel} · ` : "";
  const suffix = linkQualityLabel ? ` · ${linkQualityLabel}` : "";
  fpsLabelEl.textContent = `FastPubSub: ${edge}${fpsConnectionDetail}${suffix}`;
}

/**
 * @param {string} text
 * @param {boolean} ok
 * @param {"warn"|undefined} tone
 */
function setStatus(text, ok, tone) {
  statusEl.textContent = text;
  statusEl.className = "status";
  if (ok) {
    statusEl.classList.add("ok");
  }
  if (tone === "warn") {
    statusEl.classList.add("warn");
  }
}

coverEl.addEventListener("error", () => {
  coverEl.hidden = true;
  coverPlaceholder.hidden = false;
});

boot();
